import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./IPPatientInfoDisplay.css";
import { FaMoneyBillWave, FaArrowLeft, FaUser, FaPhoneAlt, FaIdCard, FaEye, FaBed, FaShieldAlt, FaMapMarkerAlt, FaEnvelope, FaEdit, FaSearchPlus } from "react-icons/fa";
import { toast } from "react-toastify";
import { MdContentCopy } from "react-icons/md";
import axios from "axios";
import ENDPOINTS, { API_BASE_URL } from "../../../../Constants/ApiEndpoints";
import { dateTimeFormatter, dateFormatter } from "../../../../CommonComponents/Utils/DateTimeFormatter";
import EditPatientModal from "../../../Outpatient/Patients/PatientsInfoDisplay/EditPatientModal";

const getInitials = (name) => {
  if (!name) return 'PT';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getDoctorName = (inv, defaultDoctor) => {
  if (inv.doctorName) return inv.doctorName;
  if (inv.appointmentId && typeof inv.appointmentId === 'string' && inv.appointmentId.includes('Dr: ')) {
    const parts = inv.appointmentId.split(' | ');
    if (parts.length >= 2) {
      const doc = parts[1].split('Dr: ')[1];
      if (doc) return doc.trim();
    }
  }
  return defaultDoctor || 'Dr. Vishwanath';
};

const IPPatientInfoDisplay = ({ patient, onBack }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('info');
  const [currentPatient, setCurrentPatient] = useState(patient);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [paymentHistoryData, setPaymentHistoryData] = useState(patient?.paymentPackageHistory || null);
  const initialFlagged = Boolean(patient?.flagged ?? patient?.isFlagged ?? false);
  const [isFlagged, setIsFlagged] = useState(initialFlagged);
  const [flagLoading, setFlagLoading] = useState(false);

  useEffect(() => {
    const activeFlag = patient?.flagged ?? patient?.isFlagged;
    if (activeFlag !== undefined) {
      setIsFlagged(Boolean(activeFlag));
    }
  }, [patient?.flagged, patient?.isFlagged]);

  const handleFlagClick = async () => {
    const mrNo = currentPatient?.mrNumber || patient?.mrNumber;
    if (!mrNo) return;
    const newFlagValue = !isFlagged;
    setFlagLoading(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/flagThePatient/${mrNo}/${newFlagValue}`
      );
      setIsFlagged(newFlagValue);
      setCurrentPatient(prev => ({ ...prev, flagged: newFlagValue, isFlagged: newFlagValue }));
      if (patient) {
        patient.flagged = newFlagValue;
        patient.isFlagged = newFlagValue;
      }
      toast.success("Patient flag status updated!");
    } catch (err) {
      console.error("Failed to update flag:", err);
      toast.error("Failed to update flag status.");
    } finally {
      setFlagLoading(false);
    }
  };

  // Fetch payment history data on mount or patient change if missing
  useEffect(() => {
    setCurrentPatient(patient);
    if (patient?.paymentPackageHistory) {
      setPaymentHistoryData(patient.paymentPackageHistory);
    } else if (patient?.mrNumber) {
      // Query backend for IP-specific payment history using ?patientType=IP
      axios.get(`${API_BASE_URL}/getPatientPaymentHistory/${patient.mrNumber}?patientType=IP`)
        .then(res => setPaymentHistoryData(res.data))
        .catch(err => console.warn('Failed to load payment history:', err));
    }
  }, [patient]);

  if (!currentPatient) {
    return (
      <div className="ip-patient-info-container">
        <div className="ip-master-card">
          <div className="ip-empty-text">
            <p>No patient profile data found.</p>
            <button className="ip-btn-back" onClick={onBack || (() => navigate("/inpatient/patients"))}>
              <FaArrowLeft /> Return to Patients List
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Extract admissions list for the patient
  const admissions = currentPatient.admissions || (currentPatient.admissionId ? [currentPatient] : []);
  
  // Extract all raw invoices from paymentHistoryData or patient state
  const rawInvoices = paymentHistoryData?.invoices || currentPatient.paymentPackageHistory?.invoices || currentPatient.invoices || admissions.flatMap(a => a.invoices || []);

  // Filter for Inpatient (IP) related invoices with paid amount > 0
  const invoices = rawInvoices
    .filter(inv => {
      if (!inv) return false;
      // 1. Check for explicit admissionId link
      if (inv.admissionId) return true;

      // 2. Check invoiceType property for IP/INPATIENT/ADMISSION keywords
      if (inv.invoiceType) {
        const typeStr = String(inv.invoiceType).toUpperCase();
        if (typeStr.includes('IP') || typeStr.includes('INPATIENT') || typeStr.includes('ADMISSION')) return true;
      }

      return false;
    })
    .filter(inv => {
      const paid = inv.amountPaid !== undefined && inv.amountPaid !== null
        ? inv.amountPaid
        : (inv.paidAmount !== undefined && inv.paidAmount !== null
            ? inv.paidAmount
            : (inv.ipPaidAmount !== undefined && inv.ipPaidAmount !== null
                ? inv.ipPaidAmount
                : (inv.totalAmount || inv.amount || 0)));
      return Number(paid) > 0;
    });

  const copyToClipboard = () => {
    if (!currentPatient?.mrNumber) return;
    navigator.clipboard.writeText(currentPatient.mrNumber)
      .then(() => toast.success('MR Number copied to clipboard!'))
      .catch(() => toast.error('Failed to copy MR Number'));
  };

  const hasPhoto = currentPatient.photo && currentPatient.photo.length > 20;

  return (
    <div className="ip-patient-info-container">
      <div className="ip-master-card">
        {/* === Hero Header Banner === */}
        <div className="ip-hero-banner">
          <div className="ip-hero-left">
            <div
              className="ip-avatar-wrapper"
              onClick={() => setShowPhotoModal(true)}
              style={{ position: 'relative', cursor: 'pointer' }}
              title="Click to zoom patient photo"
            >
              {hasPhoto ? (
                <img src={`data:image/jpeg;base64,${currentPatient.photo}`} alt={currentPatient.patientName} className="ip-avatar-img" />
              ) : (
                <div className="ip-avatar-circle">
                  {getInitials(currentPatient.patientName)}
                </div>
              )}

              {/* Full Hover Overlay Icon (Exact parity with PatientInfoPanel) */}
              <div className="avatar-hover-overlay">
                <FaSearchPlus size={20} color="#ffffff" />
              </div>
            </div>

            <div className="ip-hero-info">
              <div className="ip-hero-title-row">
                <h2>{currentPatient.patientName}</h2>
                <span className="ip-gender-tag">{currentPatient.gender || 'Male'}</span>
              </div>

              <div className="ip-meta-pills">
                <span className="ip-pill">
                  <FaIdCard size={12} color="#93c5fd" /> MR: <strong>{currentPatient.mrNumber}</strong>
                  <button className="ip-copy-icon-btn" onClick={copyToClipboard} title="Copy MR Number">
                    <MdContentCopy size={13} />
                  </button>
                </span>
                <span className="ip-pill">
                  Age: <strong>{currentPatient.age ? `${currentPatient.age} Yrs` : 'N/A'}</strong>
                </span>
                <span className="ip-pill">
                  <FaPhoneAlt size={11} color="#93c5fd" /> <strong>{currentPatient.phoneNumber || 'N/A'}</strong>
                </span>
                <button
                  className="priority-flag-btn"
                  onClick={handleFlagClick}
                  disabled={flagLoading}
                  style={{
                    backgroundColor: isFlagged ? "#dc2626" : "#16a34a",
                  }}
                >
                  {flagLoading ? "Processing..." : isFlagged ? "🚨 Flagged" : "🚨 Flag"}
                </button>
              </div>
            </div>
          </div>

          <div className="ip-hero-actions">
            <button className="ip-btn-edit" onClick={() => setShowEditModal(true)} title="Edit Patient Details">
              <FaEdit size={12} /> Edit Patient
            </button>
            <button className="ip-btn-back" onClick={onBack || (() => navigate("/inpatient/patients"))}>
              <FaArrowLeft size={12} /> Back to Patients List
            </button>
          </div>
        </div>

        {/* === Segmented Navigation Tabs === */}
        <div className="ip-tabs-header">
          <button
            className={`ip-tab-item ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <FaUser size={14} /> General Information
          </button>
          <button
            className={`ip-tab-item ${activeTab === 'admissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('admissions')}
          >
            <FaBed size={14} /> Admission History ({admissions.length})
          </button>
          <button
            className={`ip-tab-item ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => setActiveTab('payments')}
          >
            <FaMoneyBillWave size={14} /> Payment History ({invoices.length})
          </button>
        </div>

        {/* === Tab Content Body === */}
        <div className="ip-tab-body">
          {/* TAB 1: General Information (Upgraded UI) */}
          {activeTab === 'info' && (
            <div className="ip-info-cards-grid">
              {/* Card 1: Demographics */}
              <div className="ip-info-box border-accent-blue">
                <div className="ip-info-box-title">
                  <FaUser color="#2563eb" size={16} /> Patient Demographics
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Full Name:</span>
                  <span className="ip-info-val text-bold">{currentPatient.patientName || 'N/A'}</span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Gender:</span>
                  <span className="ip-info-val">
                    <span className="gender-badge">{currentPatient.gender || 'Male'}</span>
                  </span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Age / DOB:</span>
                  <span className="ip-info-val">
                    {currentPatient.age ? `${currentPatient.age} Years` : 'N/A'}
                    {currentPatient.dateOfBirth ? ` (${dateFormatter(currentPatient.dateOfBirth)})` : ''}
                  </span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Patient Category:</span>
                  <span className="ip-info-val">
                    <span className="patient-type-pill">INPATIENT (IP)</span>
                  </span>
                </div>
              </div>

              {/* Card 2: Contact Details */}
              <div className="ip-info-box border-accent-indigo">
                <div className="ip-info-box-title">
                  <FaPhoneAlt color="#4f46e5" size={15} /> Contact & Identification
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Phone Number:</span>
                  <span className="ip-info-val flex-center gap-2">
                    <strong>{currentPatient.phoneNumber || 'N/A'}</strong>
                    {currentPatient.phoneNumber && (
                      <button
                        className="ip-copy-icon-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(currentPatient.phoneNumber);
                          toast.success("Phone copied!");
                        }}
                        title="Copy Phone Number"
                      >
                        <MdContentCopy size={13} />
                      </button>
                    )}
                  </span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Aadhar Number:</span>
                  <span className="ip-info-val font-mono">{currentPatient.aadharNumber || 'N/A'}</span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Email:</span>
                  <span className="ip-info-val">{currentPatient.mailId || 'N/A'}</span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Address:</span>
                  <span className="ip-info-val text-wrap-address">{currentPatient.address || 'N/A'}</span>
                </div>
              </div>

              {/* Card 3: Hospital Records (Commented out per user request)
              <div className="ip-info-box border-accent-emerald">
                <div className="ip-info-box-title">
                  <FaIdCard color="#059669" size={16} /> Hospital Identification
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">MR Number:</span>
                  <span className="ip-info-val highlight">{currentPatient.mrNumber}</span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Clinic ID:</span>
                  <span className="ip-info-val">{currentPatient.clinicId || 'N/A'}</span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Reg Date:</span>
                  <span className="ip-info-val">
                    {dateFormatter(currentPatient.initialRegistrationDate || currentPatient.createdDate || currentPatient.created_at) || 'N/A'}
                  </span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Total Admissions:</span>
                  <span className="ip-info-val count-badge">{admissions.length} Record(s)</span>
                </div>
              </div>
              */}

              {/* Card 4: Current Inpatient Overview */}
              <div className="ip-info-box border-accent-amber">
                <div className="ip-info-box-title">
                  <FaBed color="#d97706" size={16} /> Current Inpatient Overview
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Current Status:</span>
                  <span className="ip-info-val">
                    <span className={admissions[0]?.dischargeDate ? 'ip-badge-discharged' : 'ip-badge-admitted'}>
                      ● {admissions[0]?.dischargeDate ? 'DISCHARGED' : 'CURRENTLY ADMITTED'}
                    </span>
                  </span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Attending Doctor:</span>
                  <span className="ip-info-val text-bold">
                    {admissions[0]?.doctorName || currentPatient.doctorName || 'Dr. Vishwanath'}
                  </span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Room / Bed:</span>
                  <span className="ip-info-val">
                    {admissions[0]?.roomType ? `${admissions[0].roomType} (${admissions[0].bedNumber || 'Bed Assigned'})` : 'General Ward'}
                  </span>
                </div>
                <div className="ip-info-row">
                  <span className="ip-info-label">Latest Admission:</span>
                  <span className="ip-info-val">
                    {admissions[0] ? dateTimeFormatter(admissions[0].admissionDate, admissions[0].admissionTime) : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: Admission History */}
          {activeTab === 'admissions' && (
            <div>
              {admissions.length === 0 ? (
                <div className="ip-empty-text">No admission records found for this patient.</div>
              ) : (
                <div className="ip-table-wrapper">
                  <table className="ip-custom-table">
                    <thead>
                      <tr>
                        <th>ADMISSION ID</th>
                        <th>ADMISSION DATE / TIME</th>
                        <th>DOCTOR</th>
                        <th>STATUS</th>
                        <th>DISCHARGE DATE</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admissions.map((adm, idx) => (
                        <tr
                          key={idx}
                          style={{ cursor: 'pointer' }}
                          onClick={() => window.open(`/inpatient/admissions/patientInfoPanel/${adm.admissionId}`, '_blank')}
                          title="Click to view billing details in new window"
                        >
                          <td>
                            <strong className="ip-info-val highlight">{adm.admissionId || 'N/A'}</strong>
                          </td>
                          <td>
                            {dateTimeFormatter(adm.admissionDate, adm.admissionTime) || 'N/A'}
                          </td>
                          <td>
                            <strong>{adm.doctorName || 'Dr. Vishwanath'}</strong>
                          </td>
                          <td>
                            <span className={adm.dischargeDate ? 'ip-badge-discharged' : 'ip-badge-admitted'}>
                              ● {adm.dischargeDate ? 'DISCHARGED' : (adm.admissionStatus || 'ADMITTED')}
                            </span>
                          </td>
                          <td>
                            {adm.dischargeDate ? dateTimeFormatter(adm.dischargeDate, adm.dischargeTime) : 'Still Admitted'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: Payment History */}
          {activeTab === 'payments' && (
            <div>
              <div className="ip-table-wrapper">
                <table className="ip-custom-table">
                  <thead>
                    <tr>
                      <th>DATE</th>
                      <th>DOCTOR</th>
                      <th>PARTICULARS</th>
                      <th>AMOUNT (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.length > 0 ? (
                      invoices.map((inv, idx) => {
                        const doctorName = getDoctorName(inv, currentPatient.doctorName || (admissions[0] && admissions[0].doctorName));
                        const targetAdmissionId = inv.admissionId || currentPatient.admissionId || (admissions[0] && admissions[0].admissionId);
                        const paidAmount = inv.amountPaid !== undefined && inv.amountPaid !== null
                          ? inv.amountPaid
                          : (inv.paidAmount !== undefined && inv.paidAmount !== null
                              ? inv.paidAmount
                              : (inv.ipPaidAmount !== undefined && inv.ipPaidAmount !== null
                                  ? inv.ipPaidAmount
                                  : (inv.totalAmount || inv.amount || 0)));

                        return (
                          <tr
                            key={idx}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (targetAdmissionId) {
                                window.open(`/inpatient/admissions/patientInfoPanel/${targetAdmissionId}`, '_blank');
                              }
                            }}
                            title="Click to view details in new window"
                          >
                            <td><strong>{dateFormatter(inv.invoiceTime || inv.createdDate || inv.date)}</strong></td>
                            <td>{doctorName}</td>
                            <td>{inv.invoiceName || inv.chargeName || 'Inpatient Charges'}</td>
                            <td><strong>₹{paidAmount}</strong></td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                          No payment records found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Patient Modal */}
      <EditPatientModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        patient={currentPatient}
        onPatientUpdated={(updatedData) => {
          setCurrentPatient((prev) => ({ ...prev, ...updatedData }));
        }}
      />

      {/* Fullscreen Photo Lightbox Modal */}
      {showPhotoModal && (
        <div className="photo-lightbox-backdrop" onClick={() => setShowPhotoModal(false)}>
          <div className="photo-lightbox-modal" onClick={(e) => e.stopPropagation()}>
            <button className="photo-lightbox-close" onClick={() => setShowPhotoModal(false)}>&times;</button>
            <img
              src={hasPhoto ? `data:image/jpeg;base64,${currentPatient.photo}` : "/VThospitalsIcon.png"}
              alt={currentPatient.patientName}
              className="photo-lightbox-img"
            />
            <div className="photo-lightbox-caption">
              <strong>{currentPatient.patientName}</strong> ({currentPatient.mrNumber})
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IPPatientInfoDisplay;
